// Router definition. Routes are declared in one place so the shell, guards, and
// navigation stay consistent and links are type-checked.
//
// Guards read the session through the shared `queryClient` rather than through
// route context, which avoids an import cycle and keeps guard logic in one spot.

// TanStack Router's control-flow API uses `throw redirect(...)`, which throws a
// `Response`-shaped Redirect value rather than an Error. The generic rule cannot
// model that, so it is disabled for this file only.
/* eslint-disable @typescript-eslint/only-throw-error */

import type { ReactNode } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { Container } from '@mantine/core';
import { ApiError } from '@/api/errors';
import * as api from '@/api/endpoints';
import { queryClient } from '@/query/client';
import { queryKeys } from '@/query/keys';
import { AppShellLayout } from '@/components/AppShell';
import { LoadingState } from '@/components/primitives';
import { useSession } from '@/session/SessionProvider';
import SignInRoute from '@/routes/SignInRoute';
import { ACCEPT_PATH, parseInviteToken, stashInvite } from '@/features/groups/invites';

/** Ensure a session exists before entering authenticated routes. */
async function requireSession(): Promise<void> {
  try {
    const session = await queryClient.ensureQueryData({
      queryKey: queryKeys.session,
      queryFn: ({ signal }) => api.getSession({ signal }),
      staleTime: 30_000,
    });
    if (!session) throw redirect({ to: '/sign-in' });
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthenticated) {
      throw redirect({ to: '/sign-in' });
    }
    // A network failure must not bounce the user to sign-in; let the screen
    // render its offline state instead.
  }
}

const rootRoute = createRootRoute({
  component: Outlet,
});

/**
 * The public landing route: the only page a signed-out visitor sees.
 *
 * It used to redirect to `/feeds` unconditionally, which meant a signed-out
 * visitor — and Google's review of the OAuth consent screen — saw nothing at the
 * URL the consent screen names. `/` now renders the same page as `/sign-in`,
 * which is where the backend sends a failed attempt's reason, so there is one
 * public screen rather than two: the description, the provider buttons and the
 * policy links.
 *
 * A signed-in visitor is redirected by the page itself, so this route only has
 * to wait out the session read.
 */
function LandingRoute(): ReactNode {
  const { state } = useSession();
  if (state.status === 'loading') {
    return (
      <Container size="xs" py="xl">
        <LoadingState />
      </Container>
    );
  }
  return <SignInRoute />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingRoute,
});

// Public pages. Nothing here is behind a session guard or the install gate: a
// reviewer, or a phone visitor deciding whether to install, must be able to read
// them.
const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/about',
  component: lazyRouteComponent(() => import('@/routes/AboutRoute')),
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: lazyRouteComponent(() => import('@/routes/PrivacyRoute')),
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: lazyRouteComponent(() => import('@/routes/TermsRoute')),
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignInRoute,
});

// An invite link, `/invite#<token>`. The token is kept for this tab and taken
// out of the address bar before anything else runs, so signing in on the way
// to the accept screen does not lose it and history does not keep it.
const inviteLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite',
  beforeLoad: () => {
    const token = parseInviteToken(window.location.hash);
    if (token) stashInvite(token);
    throw redirect({ to: ACCEPT_PATH, replace: true });
  },
});

const authenticatedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: () => requireSession(),
  component: AppShellLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/feeds',
  component: lazyRouteComponent(() => import('@/routes/DashboardRoute')),
});

const groupsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/groups',
  component: lazyRouteComponent(() => import('@/routes/GroupsRoute')),
});

const groupDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/groups/$groupId',
  component: lazyRouteComponent(() => import('@/routes/GroupDetailRoute')),
});

const inviteAcceptRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: ACCEPT_PATH,
  component: lazyRouteComponent(() => import('@/routes/InviteRoute')),
});

const sourcesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/sources',
  component: lazyRouteComponent(() => import('@/routes/SourcesRoute')),
});

const newExternalSourceRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/sources/new',
  component: lazyRouteComponent(() => import('@/routes/NewExternalSourceRoute')),
});

const sourceDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/sources/$sourceId',
  component: lazyRouteComponent(() => import('@/routes/SourceDetailRoute')),
});

const readingsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/sources/$sourceId/channels/$channelKey',
  component: lazyRouteComponent(() => import('@/routes/ReadingsRoute')),
});

const addDeviceRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/devices/new',
  component: lazyRouteComponent(() => import('@/routes/AddDeviceRoute')),
});

const configurationRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/devices/$publicId/configuration',
  component: lazyRouteComponent(() => import('@/routes/ConfigurationRoute')),
});

const modulesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/devices/$publicId/modules',
  component: lazyRouteComponent(() => import('@/routes/ModulesRoute')),
});

const wifiRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/devices/$publicId/wifi',
  component: lazyRouteComponent(() => import('@/routes/WifiRoute')),
});

const zigbeeSensorsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/devices/$publicId/zigbee',
  component: lazyRouteComponent(() => import('@/routes/ZigbeeSensorsRoute')),
});

const bleSensorsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/devices/$publicId/ble',
  component: lazyRouteComponent(() => import('@/routes/BleSensorsRoute')),
});

const accountRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/account',
  component: lazyRouteComponent(() => import('@/routes/AccountRoute')),
});

const adminRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/admin',
  beforeLoad: async () => {
    // Mirror the backend policy: only a non-impersonating admin may enter.
    // The session is read live so a stale cache cannot grant access.
    const session = await queryClient.ensureQueryData({
      queryKey: queryKeys.session,
      queryFn: ({ signal }) => api.getSession({ signal }),
      staleTime: 30_000,
    });
    if (session.role !== 'admin' || session.impersonating) {
      // An ordinary owner is sent to the landing screen rather than to the
      // source inventory, which is not where a signed-in user starts.
      throw redirect({ to: '/feeds' });
    }
  },
  component: lazyRouteComponent(() => import('@/routes/AdminRoute')),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  aboutRoute,
  privacyRoute,
  termsRoute,
  signInRoute,
  inviteLinkRoute,
  authenticatedLayoutRoute.addChildren([
    dashboardRoute,
    groupsRoute,
    groupDetailRoute,
    inviteAcceptRoute,
    sourcesRoute,
    newExternalSourceRoute,
    sourceDetailRoute,
    readingsRoute,
    addDeviceRoute,
    configurationRoute,
    modulesRoute,
    wifiRoute,
    zigbeeSensorsRoute,
    bleSensorsRoute,
    accountRoute,
    adminRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: lazyRouteComponent(() => import('@/routes/NotFoundRoute')),
});

export type AppRouter = typeof router;

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
